import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import SessionTimeoutWarning from "./components/SessionTimeoutWarning";
import { FaviconLoader } from "./components/FaviconLoader";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Admin from "./pages/Admin";
import Contact from "./pages/Contact";
import About from "./pages/About";
import Services from "./pages/Services";
import News from "./pages/News";
import Insights from "./pages/Insights";
import InsightPost from "./pages/InsightPost";
import Privacy from "./pages/Privacy";
import DataRights from "./pages/DataRights";
import Terms from "./pages/Terms";
import NotFound from "./pages/NotFound";
import Apps from "./pages/Apps";
import Resources from "./pages/Resources";
import Assessment from "./pages/Assessment";
import Unsubscribe from "./pages/Unsubscribe";
import FAQ from "./pages/FAQ";
import Testimonials from "./pages/Testimonials";
import FractionalCISOService from "./pages/services/FractionalCISOService";
import GreenfieldService from "./pages/services/GreenfieldService";
import ArchitectureService from "./pages/services/ArchitectureService";
import OutsourcingService from "./pages/services/OutsourcingService";
import TrainingService from "./pages/services/TrainingService";
import StrategyService from "./pages/services/StrategyService";
import CyberEssentialsService from "./pages/services/CyberEssentialsService";
import CyberEssentialsUK from "./pages/CyberEssentialsUK";
import CyberEssentialsCalculator from "./pages/CyberEssentialsCalculator";
import CyberEssentialsCertificationService from "./pages/services/CyberEssentialsCertificationService";
import CyberEssentialsReadinessService from "./pages/services/CyberEssentialsReadinessService";
import RiskManagementService from "./pages/services/RiskManagementService";
import ResilienceService from "./pages/services/ResilienceService";
import NISTAssessmentsService from "./pages/services/NISTAssessmentsService";
import CAFAssessmentsService from "./pages/services/CAFAssessmentsService";
import CrisisManagementService from "./pages/services/CrisisManagementService";
import EmergencyResponseService from "./pages/services/EmergencyResponseService";
import CyberSecurityLondon from "./pages/locations/CyberSecurityLondon";
import Account from "./pages/Account";
import CyberEssentialsMicroPage from "./pages/services/CyberEssentialsMicroPage";
import CyberEssentialsMicroSuccess from "./pages/services/CyberEssentialsMicroSuccess";
import CyberEssentialsMicroCancel from "./pages/services/CyberEssentialsMicroCancel";
import CyberEssentialsMicroOnboarding from "./pages/services/CyberEssentialsMicroOnboarding";
import VerifyContact from "./pages/VerifyContact";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <FaviconLoader />
        <SessionTimeoutWarning />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/staff-portal" element={<Auth />} />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requireAdmin>
                  <Admin />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/account" 
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              } 
            />
            <Route path="/contact" element={<Contact />} />
            <Route path="/verify-contact" element={<VerifyContact />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/news" element={<News />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/data-rights" element={<DataRights />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/apps" element={<Apps />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/assessment" element={<Assessment />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightPost />} />
          <Route path="/threat-intelligence" element={<News />} />
          <Route path="/news" element={<News />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/services/fractional-ciso-uk" element={<FractionalCISOService />} />
            <Route path="/services/vciso" element={<FractionalCISOService />} /> {/* Redirect old URL */}
          <Route path="/services/risk-management" element={<RiskManagementService />} />
          <Route path="/services/cyber-essentials" element={<CyberEssentialsService />} />
          <Route path="/cyber-essentials-uk" element={<CyberEssentialsUK />} />
          <Route path="/cyber-essentials-calculator" element={<CyberEssentialsCalculator />} />
          <Route path="/services/cyber-essentials-certification-uk" element={<CyberEssentialsCertificationService />} />
          <Route path="/services/cyber-essentials-readiness" element={<CyberEssentialsReadinessService />} />
          <Route path="/services/architecture" element={<ArchitectureService />} />
          <Route path="/services/greenfield" element={<GreenfieldService />} />
          <Route path="/services/outsourcing" element={<OutsourcingService />} />
          <Route path="/services/training" element={<TrainingService />} />
          <Route path="/services/strategy" element={<StrategyService />} />
          <Route path="/services/resilience" element={<ResilienceService />} />
          <Route path="/services/nist-assessments" element={<NISTAssessmentsService />} />
          <Route path="/services/caf-assessments" element={<CAFAssessmentsService />} />
          <Route path="/services/crisis-management" element={<CrisisManagementService />} />
          <Route path="/services/24-hour-emergency-response" element={<EmergencyResponseService />} />
          <Route path="/cyber-security-london" element={<CyberSecurityLondon />} />
          <Route path="/services/cyber-essentials-micro" element={<CyberEssentialsMicroPage />} />
          <Route path="/services/cyber-essentials-micro/success" element={<CyberEssentialsMicroSuccess />} />
          <Route path="/services/cyber-essentials-micro/cancel" element={<CyberEssentialsMicroCancel />} />
          <Route path="/services/cyber-essentials-micro/onboarding" element={<CyberEssentialsMicroOnboarding />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
